import clsx from "clsx";

import { LoadingSlide } from "@components/title-slide-view";
import { type Documents } from "@north-beam/nb-client-side";
import { Paginator } from "@shared/paginator";

import { DocumentsRawTable } from "./documents-raw-table";

type Doc = Documents["me"]["documents"]["data"][number];
type ContentTypes = Record<string, string>;

type Data = {
  docs: Doc[];
  contentTypes: ContentTypes;
};

export interface DocumentsTableProps {
  readonly page: number;
  readonly count: number;
  readonly pageSize: number;
  readonly error: boolean;
  readonly data: Data | null;
  readonly isRefetching: boolean;
  readonly isInitialDataLoading: boolean;
  readonly onPaginationChange: (val: number) => void;
  readonly onDeleteSuccess: () => void;
}

export const allowedPageSizes = [20, 50, 100];

export const DocumentsTable = ({
  error,
  data,
  isRefetching,
  isInitialDataLoading,
  page,
  pageSize,
  count,
  onPaginationChange,
  onDeleteSuccess,
}: DocumentsTableProps) => {
  return (
    <>
      {isInitialDataLoading || isRefetching ? (
        <div
          className={clsx(
            "flex flex-col items-center justify-center h-full w-full left-0 top-0 z-50",
            "overflow-hidden absolute",
          )}
        >
          <LoadingSlide />
        </div>
      ) : (
        <>
          {error ? (
            <div
              className={clsx(
                "flex flex-col items-center justify-center h-full w-full left-0 top-0 z-50",
                "overflow-hidden absolute",
              )}
            >
              <div className="flex flex-col items-center justify-center">
                <p className="text-lg font-medium">
                  There was an error loading the documents.
                </p>
                <i className="fa-regular fa-circle-exclamation text-danger opacity-60 text-2xl" />
              </div>
            </div>
          ) : (
            <>
              {data && (
                <div className="flex flex-col gap-4 overflow-y-hidden max-h-full">
                  {/* The right padding is for the scroll bar. */}
                  <div className="grow overflow-y-scroll pr-[15px]">
                    <DocumentsRawTable
                      data={data.docs}
                      contentTypes={data.contentTypes}
                      onDeleteSuccess={onDeleteSuccess}
                      className={clsx({
                        "opacity-20": isRefetching,
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-end">
                    <Paginator
                      page={page}
                      perPage={pageSize}
                      total={count}
                      onChange={onPaginationChange}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
